// Thin admin API client. Token is stored in localStorage (entered once via the prompt).
//
// Base URL: in dev (vite serve on localhost:5174) we go through the proxy in
// vite.config.ts which forwards /api → workers.dev. In prod (Pages free tier
// doesn't support external 200-rewrites in _redirects) we hit the worker
// directly using its absolute URL. The worker returns Access-Control-Allow-Origin: *.
const API_BASE = (() => {
  if (typeof window === 'undefined') return '';
  const h = window.location.hostname;
  // Local dev → use Vite proxy at /api
  if (h === 'localhost' || h === '127.0.0.1') return '';
  // Production → call worker directly
  return 'https://app-config.nitzan0.workers.dev';
})();

const TOKEN_KEY = 'admin_token_v2';

function token(): string {
  let t = localStorage.getItem(TOKEN_KEY) || '';
  if (!t) {
    t = window.prompt('Admin token (set via `wrangler secret put ADMIN_TOKEN`):') || '';
    if (t) localStorage.setItem(TOKEN_KEY, t);
  }
  return t;
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${token()}`);
  if (init.body && !headers.has('Content-Type') && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  const r = await fetch(API_BASE + path, { ...init, headers });
  if (r.status === 401) {
    clearToken();
    throw new Error('Unauthorized — token cleared, reload to re-enter');
  }
  const text = await r.text();
  let data: any;
  try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data as T;
}

// --- Domain types (loose — matches worker.js responses) ---
export type App = {
  id: string; name?: string; displayTitle?: string; developer?: string;
  domain?: string; enabled?: boolean; createdAt?: number; updatedAt?: number;
  metaPixelId?: string; oneSignalId?: string;
  status?: string;
};

export type SummaryStats = {
  clicks: number; installs: number; leads: number;
  ftds: number; deposits: number; revenue: number;
};

export type EventRow = {
  id: number; app_id: string; event_type: string; amount: number | null;
  currency: string | null; country: string | null; ts: number;
};

export type SpinVerticalInput = {
  name: string;
  type?: 'pwa';
  displayTitle?: string;
  developer?: string;
  brand?: string;
  icon?: string;
  defaultUrl?: string;
  locales?: string[];
  defaultLocale?: string;
  geos?: string[];
  geoRoutes?: Record<string, string>;
  metaPixelId?: string;
  metaAccessToken?: string;
  oneSignalId?: string;
  oneSignalRestKey?: string;
  gaId?: string;
  prelanderId?: string;
  // Feature settings
  autoRedirect?: number;
  installRedirectDelay?: number;
  dismissRedirectDelay?: number;
  showPreloader?: boolean;
  showHeadline?: boolean;
  showFooterMenu?: boolean;
  showShareBlock?: boolean;
  showAppSupport?: boolean;
  colorScheme?: string;
  iosFlow?: string;
  [key: string]: any; // allow extra fields
};

export type SpinVerticalResult = {
  ok?: boolean;
  appId: string;
  liveUrl?: string;
  domain?: string;
  log?: { t: number; msg: string }[];
  errors?: string[];
};

export type PushApp = {
  id: string; name: string; domain: string;
  oneSignalId: string; enabled: boolean;
};

export type PushSendBody = {
  appId: string;
  title: string;
  message: string;
  url?: string;
  iconUrl?: string;
  segments?: string[];
  countries?: string[];
  scheduledFor?: number | string;
};

export type PushNotificationRow = {
  id: string; heading: string; ts: number;
  sent: number; failed: number; opens: number; clicks: number;
};

export type PushAnalytics = {
  totals: { sent: number; delivered: number; opens: number; clicks: number };
  series: { day: string; sent: number; delivered: number; opens: number; clicks: number }[];
  notifications: PushNotificationRow[];
};

export type PushSubscription = {
  id: string; country: string; lang: string;
  lastActive: number | null; createdAt: number | null;
  sessionCount: number; device: string; invalidIdentifier: boolean;
};

export type LtvCohort = {
  day: string;
  clicks: number; installs: number; leads: number; ftds: number; deposits: number;
  revenue: number;
  installRate: number; leadRate: number; ftdRate: number; revPerClick: number;
};
export type LtvResponse = { sinceDays: number; days: LtvCohort[] };

export type BreakdownRow = {
  bucket: string;
  clicks: number; installs: number; leads: number; ftds: number; deposits: number; revenue: number;
};
export type BreakdownResponse = { sinceDays: number; by: string; rows: BreakdownRow[] };

export type TelegramTestResponse = {
  configured: boolean; hasBot: boolean; hasChat: boolean;
  result: { ok: boolean; error?: string | null; skipped?: string };
};

export type PagesProject = {
  name: string; subdomain: string; domains: string[]; productionBranch: string;
  createdAt: number | null; latestDeploymentId: string | null;
  latestStage: string | null; latestStageName: string | null;
  latestDeployedAt: number | null; latestUrl: string | null;
};

export type Zone = {
  id: string; name: string; status: string; paused: boolean;
  type: string; nameServers: string[];
  createdAt: number | null; modifiedAt: number | null; plan: string;
};

export type DomainsResponse = {
  pages: PagesProject[]; pagesError: string | null;
  zones: Zone[];          zonesError: string | null;
};

export type DomainHealth = { ok: boolean; status: number; latencyMs: number; error?: string };

export type Prelander = {
  id: string; type: string; name: string; collection?: string;
  status: string; owner?: string; assets: Record<string, string>;
  config: Record<string, any>; createdAt?: number; updatedAt?: number;
};

// --- Endpoints ---
// --- Lovables funnel ---
export type FunnelSession = {
  id: string;
  created_at: number;
  ip?: string;
  ua?: string;
  country?: string;
  idea_text?: string;
  answers?: Array<{ question: string; answer: string }>;
  app_name?: string;
  app_color?: string;
  app_features?: string[];
  mockup_url?: string;
  spec_md?: string;
  stage: 'hook' | 'chat' | 'generating' | 'revealed' | 'redirected' | 'converted';
  redirected_at?: number | null;
  conversion_at?: number | null;
  variant?: string | null;
};

export type FunnelConfig = {
  redirectUrlTemplate: string;
  affiliateId: string;
  numQuestions: number;
  ideaSuggestions: string[];
  githubRepo: string;
  githubToken: string;
  metaPixelId: string;
  metaConversionsApiToken: string;
  metaConversionsTestCode: string;
  oneSignalAppId: string;
  oneSignalRestKey: string;
  footerDisclaimer: string;
  brandName: string;
};

export type TwaManifest = {
  packageId: string;
  host: string;
  name: string;
  launcherName: string;
  display: string;
  themeColor: string;
  navigationColor?: string;
  backgroundColor: string;
  startUrl: string;
  iconUrl: string;
  maskableIconUrl?: string;
  monochromeIconUrl?: string;
  appVersion: string;
  appVersionCode: number;
  orientation?: string;
  minSdkVersion?: number;
  splashScreenFadeOutDuration?: number;
  enableNotifications?: boolean;
  fallbackType?: string;
  signingKey?: { path: string; alias: string; password?: string };
  [key: string]: any; // allow round-tripping unknown fields
};

export type BuildRun = {
  id: number;
  run_number: number;
  status: 'queued' | 'in_progress' | 'completed';
  conclusion: 'success' | 'failure' | 'cancelled' | 'timed_out' | null;
  started_at: string;
  updated_at: string;
  html_url: string;
  head_sha: string;
  head_commit_message: string;
  current_step: string | null;       // e.g. "Init Bubblewrap project"
  current_step_number: number | null;
  total_steps: number | null;
};

export type BuildRelease = {
  id: number;
  tag: string;
  name: string;
  created_at: string;
  html_url: string;
  assets: Array<{
    id: number;
    name: string;
    size: number;
    url: string;      // public browser_download_url
    api_url: string;  // GitHub API URL (for private repos)
  }>;
};

export type FunnelMetrics = {
  since: number;
  total_sessions: number;
  redirected_clicks: number;
  conversions: number;
  redirect_rate: number;
  conversion_rate: number;
  waterfall: Array<{ stage: string; count: number }>;
};

export type FunnelBucket = { bucket: string; visits: number; clicks: number; conversions: number };
export type FunnelBreakdowns = {
  since: number;
  geo: { by_country: FunnelBucket[]; by_city: FunnelBucket[] };
  device: { by_type: FunnelBucket[]; by_vendor: FunnelBucket[]; by_os: FunnelBucket[]; by_browser: FunnelBucket[] };
  sources: { by_utm_source: FunnelBucket[]; by_utm_campaign: FunnelBucket[]; by_referer: FunnelBucket[] };
  language: { by_language: FunnelBucket[] };
  behavior: {
    avg_time_to_chat?: number; avg_time_to_reveal?: number; avg_time_to_redirect?: number;
    avg_total_time?: number; avg_chat_msgs?: number; avg_chat_chars?: number;
    bot_visits?: number; total?: number;
  };
  hourly: Array<{ hour: number; visits: number; clicks: number; conversions: number }>;
};

export type FunnelSessionDetail = FunnelSession & {
  referer?: string; landing_url?: string;
  utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_term?: string; utm_content?: string;
  fbclid?: string; gclid?: string; ttclid?: string;
  city?: string; region?: string; timezone?: string; colo?: string;
  latitude?: number; longitude?: number; postal_code?: string;
  asn?: number; asn_org?: string;
  is_bot?: number; bot_score?: number;
  device_type?: string; device_vendor?: string;
  os_name?: string; os_version?: string;
  browser_name?: string; browser_version?: string;
  language?: string;
  screen_w?: number; screen_h?: number; viewport_w?: number; viewport_h?: number; dpr?: number;
  connection_type?: string; is_pwa?: number; is_twa?: number;
  time_to_chat?: number; time_to_reveal?: number; time_to_redirect?: number;
  chat_msg_count?: number; chat_total_chars?: number;
  last_seen_at?: number; total_time_ms?: number;
  conversion_at?: number; spec_md?: string; ip?: string; ua?: string;
};

export type FunnelEventRow = { id: number; event_name: string; payload: any; ts: number };

export type FunnelPushStats = {
  total_subscribers: number;
  messageable_subscribers: number;
  sent_7d: number;
  delivered_7d: number;
  clicked_7d: number;
  ctr_7d: number;
};
export type FunnelPushCampaign = {
  id: string;
  title: string;
  message: string;
  url: string;
  queued_at: number;
  send_after: number;
  completed_at: number;
  successful: number;
  failed: number;
  converted: number;
  remaining: number;
  canceled: boolean;
  platforms: { ios: number; android: number; chrome_web: number };
};
export type FunnelPushSubscriber = {
  id: string;
  device_type: number;
  device_os: string;
  device_model: string;
  country: string;
  language: string;
  timezone_id: string;
  created_at: number;
  last_active: number;
  session_count: number;
  notification_types: number;       // OneSignal: positive = subscribed, ≤ 0 = unsubscribed/denied
  invalid_identifier: boolean;
};
export type FunnelPushSend = {
  title: string;
  message: string;
  url?: string;
  iconUrl?: string;
  segments?: string[];
  scheduledFor?: number | string;
  customData?: Record<string, unknown>;
};

export const api = {
  listApps:      () => call<{ apps: Record<string, App> }>('/api/config').then(r => r.apps),
  getApp:        (id: string) => call<{ app: App }>(`/api/config/${encodeURIComponent(id)}`).then(r => r.app),
  saveApp:       (id: string, body: Partial<App> & Record<string, any>) =>
                  call<{ app: App }>(`/api/config/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }).then(r => r.app),
  toggleApp:     (id: string) =>
                  call<{ app: App }>(`/api/config/${encodeURIComponent(id)}/toggle`, { method: 'POST' }).then(r => r.app),
  deleteApp:     (id: string) =>
                  call<{ removed: boolean }>(`/api/config/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  spinVertical:  (body: SpinVerticalInput) =>
                  call<SpinVerticalResult>('/api/spin-vertical', { method: 'POST', body: JSON.stringify(body) }),
  redeployApp:   (id: string) =>
                  call<SpinVerticalResult>(`/api/redeploy/${encodeURIComponent(id)}`, { method: 'POST' }),

  summary:       (appId?: string, days = 7) =>
    call<SummaryStats>(`/api/events/summary?days=${days}${appId ? `&appId=${encodeURIComponent(appId)}` : ''}`),

  events:        (opts: { appId?: string; limit?: number; type?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.appId) q.set('appId', opts.appId);
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.type)  q.set('type', opts.type);
    return call<{ events: EventRow[] }>(`/api/events?${q}`).then(r => r.events);
  },

  listPrelanders: () => call<{ prelanders: Prelander[] }>('/api/prelanders').then(r => r.prelanders),
  getPrelander:   (id: string) => call<{ prelander: Prelander }>(`/api/prelanders/${encodeURIComponent(id)}`).then(r => r.prelander),

  // --- Push v2 ---
  pushApps:           () => call<{ apps: PushApp[] }>('/api/push/apps').then(r => r.apps),
  pushSegments:       (appId: string) => call<{ segments: string[] }>(`/api/push/segments?appId=${encodeURIComponent(appId)}`).then(r => r.segments),
  pushAnalytics:      (appId: string, days = 7) =>
                       call<PushAnalytics>(`/api/push/analytics?appId=${encodeURIComponent(appId)}&days=${days}`),
  pushSubscriptions:  (appId: string, opts: { limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams({ appId });
    if (opts.limit  != null) q.set('limit',  String(opts.limit));
    if (opts.offset != null) q.set('offset', String(opts.offset));
    return call<{ total: number; offset: number; limit: number; players: PushSubscription[] }>(`/api/push/subscriptions?${q}`);
  },
  pushSend: (body: PushSendBody) =>
    call<{ ok: boolean; notificationId?: string; recipients?: number | null }>(
      '/api/push/send', { method: 'POST', body: JSON.stringify(body) }),

  ltv:        (opts: { appId?: string; days?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.appId) q.set('appId', opts.appId);
    if (opts.days)  q.set('days',  String(opts.days));
    return call<LtvResponse>(`/api/reports/ltv?${q}`);
  },
  breakdown:  (opts: { appId?: string; days?: number; by?: 'country'|'source'|'campaign'|'app_id' } = {}) => {
    const q = new URLSearchParams();
    if (opts.appId) q.set('appId', opts.appId);
    if (opts.days)  q.set('days',  String(opts.days));
    if (opts.by)    q.set('by',    opts.by);
    return call<BreakdownResponse>(`/api/reports/breakdown?${q}`);
  },
  telegramTest: (text?: string) =>
    call<TelegramTestResponse>('/api/telegram/test', { method: 'POST', body: JSON.stringify({ text }) }),

  domains:       () => call<DomainsResponse>('/api/domains'),
  domainHealth:  (host: string) => call<DomainHealth>(`/api/domains/health?host=${encodeURIComponent(host)}`),
  purgeZone:     (zoneId: string) => call<{ ok: boolean; id: string | null }>(`/api/domains/zones/${zoneId}/purge`, { method: 'POST' }),

  uploadIcon: async (file: File) => {
    const r = await fetch(`${API_BASE}/api/icon-upload?appId=wizard`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token()}`,
        'Content-Type': file.type || 'image/png',
      },
      body: file,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data as { ok: boolean; url: string; key: string };
  },

  funnelSessions: (limit = 100, offset = 0, stage?: string) => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (stage) qs.set('stage', stage);
    return call<{ sessions: FunnelSession[] }>(`/api/funnel/sessions?${qs}`).then(r => r.sessions);
  },
  funnelMetrics: (sinceMs?: number) => {
    const qs = sinceMs ? `?since=${sinceMs}` : '';
    return call<FunnelMetrics>(`/api/funnel/metrics${qs}`);
  },
  funnelBreakdowns: (sinceMs?: number) => {
    const qs = sinceMs ? `?since=${sinceMs}` : '';
    return call<FunnelBreakdowns>(`/api/funnel/breakdowns${qs}`);
  },
  funnelSession: (id: string) => call<{ session: FunnelSessionDetail; events: FunnelEventRow[] }>(`/api/funnel/session/${id}`),

  // ── Funnel push (OneSignal, funnel-scoped) ──
  funnelPushValidate:    () => call<{ valid: boolean; name?: string; players?: number; messageable_players?: number; site_url?: string; error?: string }>('/api/funnel/push/validate'),
  funnelPushStats:       () => call<FunnelPushStats>('/api/funnel/push/stats'),
  funnelPushHistory:     (limit = 20) => call<{ campaigns: FunnelPushCampaign[] }>(`/api/funnel/push/history?limit=${limit}`).then(r => r.campaigns),
  funnelPushSubscribers: (limit = 50, offset = 0) => call<{ total: number; subscribers: FunnelPushSubscriber[] }>(`/api/funnel/push/subscribers?limit=${limit}&offset=${offset}`),
  funnelPushSend:        (body: FunnelPushSend) => call<{ ok: boolean; id: string; recipients: number; attempt?: string }>('/api/funnel/push/send', { method: 'POST', body: JSON.stringify(body) }),
  funnelConfig:    () => call<FunnelConfig>('/api/funnel/config'),
  saveFunnelConfig: (cfg: Partial<FunnelConfig>) =>
    call<FunnelConfig>('/api/funnel/config', { method: 'PUT', body: JSON.stringify(cfg) }),

  // Build (GitHub Actions)
  triggerBuild:    () => call<{ triggered: boolean; repo: string }>('/api/funnel/build/trigger', { method: 'POST' }),
  buildRuns:       () => call<{ runs: BuildRun[] }>('/api/funnel/build/runs').then(r => r.runs),
  buildReleases:   () => call<{ releases: BuildRelease[] }>('/api/funnel/build/releases').then(r => r.releases),
  validateBuild:   () => call<{ valid: boolean; workflow_state: string; workflow_name: string; repo: string }>('/api/funnel/build/validate'),
  buildDownloadUrl: (apiUrl: string) => `${API_BASE}/api/funnel/build/download?url=${encodeURIComponent(apiUrl)}`,
  buildManifest:   () => call<{ sha: string; manifest: TwaManifest }>('/api/funnel/build/manifest'),
  saveBuildManifest: (manifest: TwaManifest, message?: string) =>
    call<{ ok: boolean; commit_sha: string; new_version_code: number }>('/api/funnel/build/manifest', { method: 'PUT', body: JSON.stringify({ manifest, message }) }),
  uploadAppIcon: async (file: File, opts: { removeBg?: boolean } = {}) => {
    const removeBg = opts.removeBg !== false;
    const r = await fetch(`${API_BASE}/api/funnel/build/icon?remove_bg=${removeBg}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token()}`,
        'Content-Type': file.type || 'image/png',
      },
      body: file,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data as {
      ok: boolean;
      bg_removed: boolean;
      size: number;
      r2_url: string;
      icon_commit: string;
      manifest_commit: string;
      new_version_code: number;
    };
  },

  uploadAsset: async (prelanderId: string, slot: string, file: File) => {
    const r = await fetch(`${API_BASE}/api/prelanders/${encodeURIComponent(prelanderId)}/asset/${encodeURIComponent(slot)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token()}`,
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data as { ok: boolean; slot: string; url: string; key: string };
  },
};
